import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payments';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getOutsourceRepairs,
  getOutsourceTechnicianDashboard,
  updateOutsourcePayment
} from '../../services/api';
import { canViewOutsourcePrice } from '../../utils/auth';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const money = (v) =>
  `Rs. ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusColor = (s) => {
  if (['completed', 'delivered'].includes(s)) return 'success';
  if (['cancelled'].includes(s)) return 'default';
  if (['in_progress', 'approved'].includes(s)) return 'info';
  return 'warning';
};

const payStatusColor = (s) => (s === 'paid' ? 'success' : 'warning');

const methodLabel = (m) => {
  if (m === 'cash') return 'Cash';
  if (m === 'cheque') return 'Cheque';
  if (m === 'online_bank_transfer') return 'Online Bank Transfer';
  return m || '—';
};

const emptyPayForm = (repair) => {
  const jobCost =
    Number(repair?.outsource_repair_charges || 0) + Number(repair?.outsource_transport_charges || 0);
  return {
    outsource_payment_status: repair?.outsource_payment_status === 'paid' ? 'paid' : 'pending',
    outsource_payment_method: repair?.outsource_payment_method || 'cash',
    outsource_payment_amount: repair?.outsource_payment_amount ?? jobCost,
    outsource_payment_date: repair?.outsource_payment_date || new Date().toISOString().slice(0, 10),
    outsource_payment_reference: repair?.outsource_payment_reference || '',
    outsource_payment_details: repair?.outsource_payment_details || '',
    outsource_cheque_number: repair?.outsource_cheque_number || '',
    outsource_cheque_date: repair?.outsource_cheque_date || '',
    outsource_bank_name: repair?.outsource_bank_name || ''
  };
};

const OutsourceRepairs = () => {
  const navigate = useNavigate();
  const { technicianName } = useParams();
  const decodedName = technicianName ? decodeURIComponent(technicianName) : null;
  const showPrice = canViewOutsourcePrice();

  const [search, setSearch] = useState('');
  const [payFilter, setPayFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [overview, setOverview] = useState(null);
  const [techDash, setTechDash] = useState(null);

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payRepair, setPayRepair] = useState(null);
  const [payForm, setPayForm] = useState(emptyPayForm());
  const [savingPay, setSavingPay] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getOutsourceRepairs({
        technician: search || undefined,
        payment_status: payFilter || undefined
      });
      setOverview(res.data);
      setTechDash(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load outsource repairs'));
    } finally {
      setLoading(false);
    }
  }, [search, payFilter]);

  const loadTech = useCallback(async () => {
    if (!decodedName) return;
    try {
      setLoading(true);
      setError('');
      const res = await getOutsourceTechnicianDashboard(decodedName);
      setTechDash(res.data);
      setOverview(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load technician dashboard'));
    } finally {
      setLoading(false);
    }
  }, [decodedName]);

  useEffect(() => {
    if (decodedName) loadTech();
    else loadOverview();
  }, [decodedName, loadOverview, loadTech]);

  const openTech = (name) => {
    navigate(`/repairs/outsource/technician/${encodeURIComponent(name)}`);
  };

  const openPayDialog = (repair) => {
    setPayRepair(repair);
    setPayForm(emptyPayForm(repair));
    setPayDialogOpen(true);
  };

  const savePayment = async () => {
    if (!payRepair?.id) return;
    try {
      setSavingPay(true);
      setError('');
      const payload =
        payForm.outsource_payment_status === 'pending'
          ? { outsource_payment_status: 'pending', outsource_payment_details: payForm.outsource_payment_details }
          : { ...payForm, outsource_payment_status: 'paid' };
      const res = await updateOutsourcePayment(payRepair.id, payload);
      if (res?.success === false) {
        setError(res.message || 'Failed to update payment');
        return;
      }
      setSuccess(payForm.outsource_payment_status === 'paid' ? 'Marked as paid' : 'Marked as pending');
      setTimeout(() => setSuccess(''), 3000);
      setPayDialogOpen(false);
      if (decodedName) await loadTech();
      else await loadOverview();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update outsource payment'));
    } finally {
      setSavingPay(false);
    }
  };

  const PaymentDialog = (
    <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon color="primary" />
          Outsource Payment
        </Box>
        <Typography variant="body2" color="text.secondary">
          {payRepair?.grn_number || payRepair?.service_number || payRepair?.repair_number} ·{' '}
          {payRepair?.outsource_technician_name || 'Technician'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Payment status"
              value={payForm.outsource_payment_status}
              onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_status: e.target.value }))}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>
          </Grid>
          {payForm.outsource_payment_status === 'paid' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment method"
                  value={payForm.outsource_payment_method}
                  onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_method: e.target.value }))}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="online_bank_transfer">Online Bank Transfer</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount paid"
                  value={payForm.outsource_payment_amount}
                  onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_amount: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Payment date"
                  InputLabelProps={{ shrink: true }}
                  value={payForm.outsource_payment_date}
                  onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_date: e.target.value }))}
                />
              </Grid>
              {payForm.outsource_payment_method === 'cheque' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cheque number"
                      required
                      value={payForm.outsource_cheque_number}
                      onChange={(e) => setPayForm((f) => ({ ...f, outsource_cheque_number: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Cheque date"
                      InputLabelProps={{ shrink: true }}
                      value={payForm.outsource_cheque_date}
                      onChange={(e) => setPayForm((f) => ({ ...f, outsource_cheque_date: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Bank name"
                      value={payForm.outsource_bank_name}
                      onChange={(e) => setPayForm((f) => ({ ...f, outsource_bank_name: e.target.value }))}
                    />
                  </Grid>
                </>
              )}
              {payForm.outsource_payment_method === 'online_bank_transfer' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Transfer reference"
                      required
                      value={payForm.outsource_payment_reference}
                      onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_reference: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank name"
                      value={payForm.outsource_bank_name}
                      onChange={(e) => setPayForm((f) => ({ ...f, outsource_bank_name: e.target.value }))}
                    />
                  </Grid>
                </>
              )}
              {payForm.outsource_payment_method === 'cash' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reference / receipt # (optional)"
                    value={payForm.outsource_payment_reference}
                    onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_reference: e.target.value }))}
                  />
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes / details"
              value={payForm.outsource_payment_details}
              onChange={(e) => setPayForm((f) => ({ ...f, outsource_payment_details: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPayDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={savePayment} disabled={savingPay}>
          {savingPay ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const payLabel = (r) => {
    if (r.outsource_payment_status === 'paid') {
      return `Paid (${methodLabel(r.outsource_payment_method)}${r.outsource_payment_date ? ` · ${r.outsource_payment_date}` : ''})`;
    }
    return 'Pending';
  };

  const outsourceJobColumns = (includeTechnician = false) => [
    { label: 'GRN / Service', value: (r) => dash(r.grn_number || r.service_number || r.repair_number) },
    ...(includeTechnician
      ? [{ label: 'Technician', value: (r) => dash(r.outsource_technician_name || 'Unassigned') }]
      : []),
    { label: 'Customer', value: (r) => dash(r.customer_name) },
    { label: 'Device', value: (r) => [r.device_type, r.device_model].filter(Boolean).join(' ') || '-' },
    { label: 'Status', value: (r) => fmtExportStatus(r.status) },
    { label: 'Received', value: (r) => fmtExportDate(r.received_date) },
    ...(showPrice
      ? [
          { label: 'Repair charges', align: 'right', value: (r) => fmtExportMoney(r.outsource_repair_charges) },
          { label: 'Transport', align: 'right', value: (r) => fmtExportMoney(r.outsource_transport_charges) }
        ]
      : []),
    { label: 'Tech payment', value: payLabel }
  ];

  const renderPayCell = (r) => {
    const paid = r.outsource_payment_status === 'paid';
    return (
      <Box>
        <Chip
          size="small"
          label={paid ? 'Paid' : 'Pending'}
          color={payStatusColor(paid ? 'paid' : 'pending')}
          sx={{ mb: 0.5 }}
        />
        {paid && (
          <Typography variant="caption" display="block" color="text.secondary">
            {methodLabel(r.outsource_payment_method)}
            {r.outsource_payment_date ? ` · ${r.outsource_payment_date}` : ''}
          </Typography>
        )}
        {showPrice && (
          <Button size="small" startIcon={<PaymentIcon />} onClick={() => openPayDialog(r)} sx={{ mt: 0.5 }}>
            {paid ? 'Edit' : 'Mark paid'}
          </Button>
        )}
      </Box>
    );
  };

  if (decodedName && techDash) {
    const { summary, repairs } = techDash;
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/repairs/outsource')} sx={{ mb: 2 }}>
          All outsource technicians
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography variant="h4" gutterBottom>
            Outsource Technician: {summary.technician_name}
          </Typography>
          <ExportButtons
            title={`Outsource Technician: ${summary.technician_name}`}
            filename={`outsource_${(summary.technician_name || 'technician').replace(/\s+/g, '_').toLowerCase()}`}
            landscape
            subtitle={`Jobs ${summary.total_jobs} · Active ${summary.active_jobs} · Completed ${summary.completed_jobs}`}
            summary={showPrice ? [
              { label: 'Paid to technician', value: money(summary.paid_amount) },
              { label: 'Pending to pay', value: money(summary.pending_amount) },
              { label: 'Total outsource cost', value: money(summary.total_outsource_cost), emphasis: true },
              { label: 'Customer revenue', value: money(summary.customer_revenue) }
            ] : [
              { label: 'Total jobs', value: summary.total_jobs },
              { label: 'Active', value: summary.active_jobs },
              { label: 'Completed', value: summary.completed_jobs }
            ]}
            columns={outsourceJobColumns(false)}
            rows={repairs || []}
            onError={setError}
          />
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card><CardContent>
              <Typography color="text.secondary">Total jobs</Typography>
              <Typography variant="h5">{summary.total_jobs}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card><CardContent>
              <Typography color="text.secondary">Active / doing</Typography>
              <Typography variant="h5">{summary.active_jobs}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card><CardContent>
              <Typography color="text.secondary">Completed</Typography>
              <Typography variant="h5">{summary.completed_jobs}</Typography>
            </CardContent></Card>
          </Grid>
          {showPrice && (
            <>
              <Grid item xs={6} sm={4} md={3}>
                <Card><CardContent>
                  <Typography color="text.secondary">Paid to technician</Typography>
                  <Typography variant="h6" color="success.main">{money(summary.paid_amount)}</Typography>
                  <Typography variant="caption">{summary.paid_jobs || 0} jobs</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card><CardContent>
                  <Typography color="text.secondary">Pending to pay</Typography>
                  <Typography variant="h6" color="warning.main">{money(summary.pending_amount)}</Typography>
                  <Typography variant="caption">{summary.pending_jobs || 0} jobs</Typography>
                </CardContent></Card>
              </Grid>
            </>
          )}
        </Grid>

        {showPrice && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1">
              Total outsource cost: <strong>{money(summary.total_outsource_cost)}</strong>
              {' · '}
              Customer revenue: <strong>{money(summary.customer_revenue)}</strong>
            </Typography>
          </Paper>
        )}

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>GRN / Service</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Received</TableCell>
                {showPrice && <TableCell align="right">Repair ch.</TableCell>}
                {showPrice && <TableCell align="right">Transport</TableCell>}
                <TableCell>Tech payment</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(repairs || []).map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.grn_number || r.service_number || r.repair_number}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>
                  <TableCell>{r.device_type} {r.device_model || ''}</TableCell>
                  <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                  <TableCell>{r.received_date}</TableCell>
                  {showPrice && <TableCell align="right">{money(r.outsource_repair_charges)}</TableCell>}
                  {showPrice && <TableCell align="right">{money(r.outsource_transport_charges)}</TableCell>}
                  <TableCell>{renderPayCell(r)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/repairs/${r.id}`)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        {PaymentDialog}
      </Container>
    );
  }

  const technicians = overview?.technicians || [];
  const repairs = overview?.repairs || [];
  const totals = overview?.totals || {};

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>Outsource Repairs</Typography>
          <Typography color="text.secondary">
            Monitor outsource jobs, technician costs, and paid / pending charges
          </Typography>
        </Box>
        <ExportButtons
          title="Outsource Repairs"
          filename="outsource_repairs"
          landscape
          summary={showPrice ? [
            { label: 'Jobs', value: totals.jobs || 0 },
            { label: 'Technicians', value: totals.technicians || 0 },
            { label: 'Repair charges', value: money(totals.repair_charges) },
            { label: 'Transport', value: money(totals.transport_charges) },
            { label: 'Total cost', value: money(totals.total_outsource_cost), emphasis: true },
            { label: 'Paid', value: `${money(totals.paid_amount)} (${totals.paid_jobs || 0})` },
            { label: 'Pending', value: `${money(totals.pending_amount)} (${totals.pending_jobs || 0})` }
          ] : [
            { label: 'Jobs', value: totals.jobs || 0 },
            { label: 'Technicians', value: totals.technicians || 0 }
          ]}
          extraSections={[
            {
              title: 'Technicians',
              columns: [
                { label: 'Technician', value: (t) => dash(t.technician_name) },
                { label: 'Total jobs', value: (t) => t.total_jobs },
                { label: 'Doing', value: (t) => t.active_jobs },
                { label: 'Done', value: (t) => t.completed_jobs },
                ...(showPrice
                  ? [
                      { label: 'Cost', align: 'right', value: (t) => money(t.total_outsource_cost) },
                      { label: 'Paid', align: 'right', value: (t) => money(t.paid_amount) },
                      { label: 'Pending', align: 'right', value: (t) => money(t.pending_amount) }
                    ]
                  : [])
              ],
              rows: technicians
            }
          ]}
          columns={outsourceJobColumns(true)}
          rows={repairs}
          disabled={!repairs.length && !technicians.length}
          onError={setError}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Filter technician"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadOverview()}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment status"
              value={payFilter}
              onChange={(e) => setPayFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={loadOverview} disabled={loading}>
              {loading ? 'Loading…' : 'Search'}
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Chip label={`Jobs: ${totals.jobs || 0}`} />
          <Chip label={`Technicians: ${totals.technicians || 0}`} />
          {showPrice && (
            <>
              <Chip label={`Repair charges: ${money(totals.repair_charges)}`} color="secondary" />
              <Chip label={`Transport: ${money(totals.transport_charges)}`} color="secondary" />
              <Chip label={`Total cost: ${money(totals.total_outsource_cost)}`} color="warning" />
              <Chip label={`Paid: ${money(totals.paid_amount)} (${totals.paid_jobs || 0})`} color="success" />
              <Chip label={`Pending: ${money(totals.pending_amount)} (${totals.pending_jobs || 0})`} color="warning" />
            </>
          )}
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>Technicians</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {technicians.length === 0 && !loading && (
          <Grid item xs={12}>
            <Typography color="text.secondary">No outsource repairs found.</Typography>
          </Grid>
        )}
        {technicians.map((t) => (
          <Grid item xs={12} sm={6} md={4} key={t.technician_name}>
            <Card>
              <CardActionArea onClick={() => openTech(t.technician_name)}>
                <CardContent>
                  <Typography variant="h6">{t.technician_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total {t.total_jobs} · Doing {t.active_jobs} · Done {t.completed_jobs}
                  </Typography>
                  {showPrice && (
                    <>
                      <Typography sx={{ mt: 1 }}>
                        Cost: <strong>{money(t.total_outsource_cost)}</strong>
                      </Typography>
                      <Typography variant="caption" display="block" color="success.main">
                        Paid {money(t.paid_amount)} · Pending {money(t.pending_amount)}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" gutterBottom>All outsource jobs</Typography>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>GRN / Service</TableCell>
              <TableCell>Technician</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              {showPrice && <TableCell align="right">Repair ch.</TableCell>}
              {showPrice && <TableCell align="right">Transport</TableCell>}
              <TableCell>Tech payment</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {repairs.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.grn_number || r.service_number || r.repair_number}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openTech(r.outsource_technician_name || 'Unassigned')}>
                    {r.outsource_technician_name || 'Unassigned'}
                  </Button>
                </TableCell>
                <TableCell>{r.customer_name}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                {showPrice && <TableCell align="right">{money(r.outsource_repair_charges)}</TableCell>}
                {showPrice && <TableCell align="right">{money(r.outsource_transport_charges)}</TableCell>}
                <TableCell>{renderPayCell(r)}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => navigate(`/repairs/${r.id}`)}>Open</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {PaymentDialog}
    </Container>
  );
};

export default OutsourceRepairs;
